import React from 'react';
import DashBoardDialog from './DashBoardDialog';
import dayjs from 'dayjs';

/**
 * 스케줄 상세 다이얼로그 (안전보건 일정)
 *
 * @param {boolean}  open           - 다이얼로그 열림 여부
 * @param {function} onClose        - 닫기 콜백
 * @param {object}   scheduleData   - 스케줄 데이터
 *
 * scheduleData shape:
 * {
 *   title         : string,
 *   startDateTime : string,   // 예: "2023.11.24 14:00"
 *   endDateTime   : string,   // 예: "2023.11.24 16:00"
 *   calendar      : string,
 *   calendarColor : string,   // 기본: '#77b054'
 *   isAllDay      : boolean,
 *   location      : string,
 *   description   : string,
 * }
 */

// 캘린더 아이콘 SVG
const CalendarIcon = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`w-3.5 h-3.5 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// 위치 아이콘 SVG
const LocationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-3.5 h-3.5 text-[#77b054]"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

function ScheduleDetailDialog({
  open = false,
  onClose = () => {},
  scheduleData = {},
}) {
  const fields = [
    {
      label: '일정',
      value: scheduleData.title || '-',
      span: 2,
    },
    {
      label: '시작 일시',
      value: (
        <span className="flex items-center gap-1.5 text-sm text-[#2c3e50]">
          <CalendarIcon className="text-[#77b054]" />
          {scheduleData.allDay === 'Y'
            ? dayjs(scheduleData.startDt).format('YYYY.MM.DD')
            : dayjs(scheduleData.startDt).format('YYYY.MM.DD HH:mm')}
        </span>
      ),
    },
    {
      label: '종료 일시',
      value: (
        <span className="flex items-center gap-1.5 text-sm text-[#2c3e50]">
          <CalendarIcon className="text-[#77b054]" />
          {scheduleData.allDay === 'Y'
            ? dayjs(scheduleData.endDt).format('YYYY.MM.DD')
            : dayjs(scheduleData.endDt).format('YYYY.MM.DD HH:mm')}
        </span>
      ),
    },
    {
      label: '내 캘린더',
      value: (
        <span className="flex items-center gap-2 text-sm text-[#2c3e50]">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: scheduleData.color || '#77b054' }}
          />
          {scheduleData.calendarName || '안전보건 일정'}
        </span>
      ),
    },
    {
      label: '종일 여부',
      value: (
        <span
          className={`text-sm ${
            scheduleData.allDay === 'Y'
              ? 'text-[#2c3e50]'
              : 'text-gray-400 italic'
          }`}
        >
          {scheduleData.allDay === 'Y' ? '예' : '아니오'}
        </span>
      ),
    },
    {
      label: '장소',
      value: (
        <span className="flex items-center gap-1.5 text-sm text-[#2c3e50]">
          <LocationIcon />
          {scheduleData.location || '-'}
        </span>
      ),
      span: 2,
    },
    {
      label: '내용',
      value: (
        <span className="text-sm text-[#2c3e50] leading-relaxed whitespace-pre-line">
          {scheduleData.description || '-'}
        </span>
      ),
      span: 2,
      tall: true,
    },
  ];

  return (
    <DashBoardDialog
      open={open}
      onClose={onClose}
      title="스케줄 상세"
      fields={fields}
      size="md"
      actions={
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm font-bold bg-white text-[#7f8c8d] border border-[#e1e8ed] hover:bg-gray-100 transition-colors"
          >
            닫기
          </button>
        </div>
      }
    />
  );
}

export default ScheduleDetailDialog;

// ─── 사용 예시 ──────────────────────────────────────────────────────────────
// <ScheduleDetailDialog
//   open={open}
//   onClose={() => setOpen(false)}
//   scheduleData={{
//     title: '전사 정기 소방 합동 훈련',
//     startDateTime: '2023.11.24 14:00',
//     endDateTime: '2023.11.24 16:00',
//     calendar: '안전보건 일정',
//     calendarColor: '#77b054',
//     isAllDay: false,
//     location: '본사 대운동장 및 1~3호 생산 라인',
//     description:
//       '2023년 하반기 전사 소방 합동 훈련 실시 건\n\n' +
//       '1. 훈련 일시: 2023.11.24 14:00 - 16:00\n' +
//       '2. 참가 대상: 전 임직원 및 상주 협력사\n' +
//       '3. 주요 내용: 화재 경보 발령 시 대피 요령 숙지 및 소화 설비 사용 실습\n' +
//       '4. 특이 사항: 대피로 안내 요원 배치 및 안전보건팀 현장 지도 실시',
//   }}
// />
